/**
 * TAK Protocol protobuf encoder/decoder using protobufjs.
 *
 * Provides browser-safe encoding and decoding of TakMessage protobuf
 * messages. Uses a JSON descriptor instead of loading .proto files from
 * the filesystem, making it compatible with CRA/webpack bundling.
 */

import protobuf from "protobufjs";
import { TAK_PROTO_DESCRIPTOR } from "./takProtoDescriptor";

const root = protobuf.Root.fromJSON(TAK_PROTO_DESCRIPTOR);

const TakMessage = root.lookupType(
    "atakmap.commoncommo.protobuf.v1.TakMessage"
);

/** Plain-object representation of a CotEvent for encoding. */
export interface CotEventPayload {
    type: string;
    uid: string;
    how: string;
    sendTime: number;
    startTime: number;
    staleTime: number;
    lat: number;
    lon: number;
    hae: number;
    ce: number;
    le: number;
    access?: string;
    qos?: string;
    opex?: string;
    detail?: {
        xmlDetail?: string;
        contact?: { endpoint?: string; callsign?: string };
        group?: { name?: string; role?: string };
        precisionLocation?: { geopointsrc?: string; altsrc?: string };
        status?: { battery?: number };
        takv?: {
            device?: string;
            platform?: string;
            os?: string;
            version?: string;
        };
        track?: { speed?: number; course?: number };
    };
}

/** Decoded TakMessage contents. */
export interface DecodedTakMessage {
    takControl?: {
        minProtoVersion?: number;
        maxProtoVersion?: number;
        contactUid?: string;
    };
    cotEvent?: CotEventPayload;
}

/**
 * Encode a TakMessage payload to a Uint8Array (protobuf binary).
 *
 * @param payload - Plain object conforming to TakMessage schema.
 *                  Typically `{ cotEvent: { ... } }`.
 * @returns Encoded protobuf bytes.
 * @throws If the payload fails protobuf verification.
 */
export function encodeTakMessage(payload: {
    takControl?: DecodedTakMessage["takControl"];
    cotEvent?: CotEventPayload;
}): Uint8Array {
    const errMsg = TakMessage.verify(payload);
    if (errMsg) {
        throw new Error(`TakMessage verification failed: ${errMsg}`);
    }
    const message = TakMessage.create(payload);
    return TakMessage.encode(message).finish();
}

/**
 * Decode protobuf bytes into a TakMessage plain object.
 *
 * @param data - Uint8Array or Buffer containing serialized TakMessage bytes.
 * @returns Decoded message as a plain JS object.
 */
export function decodeTakMessage(
    data: Uint8Array | Buffer
): DecodedTakMessage {
    const message = TakMessage.decode(
        data instanceof Uint8Array ? data : new Uint8Array(data)
    );
    return TakMessage.toObject(message, {
        longs: Number,
        enums: String,
        defaults: true,
    }) as DecodedTakMessage;
}
